import {
  AssetType,
  AvailabilityMetricMethod,
  EEGRegime,
  GermanyFederalState,
  OptimizationGroupParametersType,
  OptimizationParameterValue,
  TargetRatedPowerGroup,
} from '@e2m-customer-portal/domains';
import { mustBeDivisibleBy, mustBeGreaterThan, Nullable, Validation, validation } from '@e2m-customer-portal/shared';
import {
  DayOfWeek,
  InfoTooltip,
  NumberInput,
  TextInput,
  TimePicker,
  ValidatedContainer,
  WeekdaySelection,
  WeekdaySelector,
} from '@e2m-customer-portal/ui';
import { faker } from '@faker-js/faker/locale/de';
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import clsx from 'clsx';
import { getHours, getMinutes, roundToNearestMinutes, setHours, setMinutes } from 'date-fns';
import { getOptimizationSchedule, OptimizationSchedule } from 'domains/optimization/utils';
import { equals, range } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { validatedInput } from 'utils';
import { OptimizationHorizonMode } from './types';

function getWeekdaySelectionValidation(message: string): Validation<WeekdaySelection | undefined> {
  return validation(message, (value) => value != null && range(0, 7).some((day) => value[day as DayOfWeek]));
}

const ValidatedWeekdaySelector = validatedInput(WeekdaySelector, 'selected');

interface OptimizationGroupParametersProps {
  data: OptimizationGroupParametersType | null;
  updateValues: (parameters: string) => void;
  assetType?: AssetType;
  isSaving?: boolean;
  readOnly: (parameter?: string, specialCondition?: boolean) => boolean;
}

export function OptimizationGroupParameters({
  data,
  updateValues,
  assetType,
  isSaving,
  readOnly,
}: OptimizationGroupParametersProps) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const [isValid, setIsValid] = useState(false);
  const inputClass = 'w-full bg-white';

  const weekdaySelectionRules = useMemo(
    () => [getWeekdaySelectionValidation(t('FORECAST_ASSISTANT_ERROR_SELECT_WEEKDAYS'))],
    [t],
  );

  if (!data) return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE')}</div>;

  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [updatedBillingDataConfig, setUpdatedBillingDataConfig] = useState(data.billing_data_config);
  const [updatedTargetRatedPowerGroups, setUpdatedTargetRatedPowerGroups] = useState(data.target_rated_power_groups);
  const [updatedData, setUpdatedData] = useState<OptimizationGroupParametersType>(
    data ? { ...data } : ({} as OptimizationGroupParametersType),
  );
  const [optimizationSchedule, setOptimizationSchedule] = useState<OptimizationSchedule | null>(
    getOptimizationSchedule(data.opt_run_schedule ?? null),
  );

  const updateOptimizationGroupParametersValues = (isValid: boolean) => {
    if (isValid) {
      const updatedItems = Object.fromEntries(changes);
      const mutationBody = JSON.stringify({
        asset_type: assetType,
        ...updatedItems,
      });
      updateValues(mutationBody);
      setChanges(new Map());
    }
  };

  const resetValues = () => {
    setUpdatedBillingDataConfig(data.billing_data_config);
    setUpdatedTargetRatedPowerGroups(data.target_rated_power_groups);
    setUpdatedData({ ...data } as OptimizationGroupParametersType);
    setChanges(new Map());
    setOptimizationSchedule(getOptimizationSchedule(data.opt_run_schedule ?? null));
  };

  // Reset values when switching between test data sets
  useEffect(resetValues, [data]);

  useEffect(() => {
    if (updatedBillingDataConfig && !equals(updatedBillingDataConfig, data.billing_data_config)) {
      setChanges(changes.set('billing_data_config', updatedBillingDataConfig));
      setUpdatedData({ ...updatedData, billing_data_config: updatedBillingDataConfig });
    }
  }, [updatedBillingDataConfig]);

  useEffect(() => {
    if (
      updatedTargetRatedPowerGroups &&
      !equals(updatedTargetRatedPowerGroups, updatedData?.target_rated_power_groups)
    ) {
      setUpdatedData({ ...updatedData, target_rated_power_groups: updatedTargetRatedPowerGroups });
      setChanges(changes.set('target_rated_power_groups', updatedTargetRatedPowerGroups));
    }
  }, [updatedTargetRatedPowerGroups]);

  return (
    <div
      className="p-6 m-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-grey-50 border-solid border border-divider rounded-md"
      data-testid="group-parameters"
    >
      {!readOnly('meta') && (
        <>
          <h2 className="mt-0 text-base font-semibold xl:col-span-2">{t('COPT_GROUP_META')}</h2>
          {updatedData.name !== undefined && (
            <TextInput
              value={updatedData.name}
              name="name"
              className="w-full bg-white"
              label={t('COPT_GROUP_NAME')}
              disabled
            />
          )}
          {updatedData.status !== undefined && (
            <TextInput
              value={updatedData.status}
              name="status"
              className="w-full bg-white"
              label={t('COPT_STATUS')}
              disabled
            />
          )}
          {updatedData.valid_from_timestamp !== undefined && (
            <TextInput
              value={updatedData.valid_from_timestamp}
              name="valid_from_timestamp"
              className="w-full bg-white"
              label={t('COPT_VALID_FROM_TIMESTAMP')}
              disabled
            />
          )}
          {updatedData.asset_type !== undefined && (
            <TextInput
              value={updatedData.asset_type}
              name="asset_type"
              className="w-full bg-white"
              label={t('COPT_ASSET_TYPE')}
              disabled
            />
          )}
        </>
      )}
      <h2 className={clsx('text-base font-semibold xl:col-span-2', { 'mt-0': !readOnly('meta') })}>
        {t('COPT_GROUP_PARAMETERS_TITLE', { asset_type: assetType })}
      </h2>
      {updatedData.power_plant_serial_number !== undefined && (
        <p className="text-grey-400 text-sm font-semibold xl:col-span-2">
          {`${t('COPT_POWER_PLANT_SERIAL_NUMBER')} `}
          <InfoTooltip field="power_plant_serial_number" />
        </p>
      )}
      {updatedData.melo_id !== undefined && (
        <p className="text-grey-400 text-sm font-semibold xl:col-span-2">
          {`${t('COPT_MELO_ID')} `} <InfoTooltip field="melo_id" />
        </p>
      )}

      <ValidatedContainer validate="on" onValidated={setIsValid}>
        {updatedData.use_scheduling !== undefined && (
          <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
            <input
              type="checkbox"
              checked={!!updatedData.use_scheduling}
              disabled={readOnly('use_scheduling')}
              onChange={() => {
                const use_scheduling = !updatedData.use_scheduling;
                setUpdatedData({ ...updatedData, use_scheduling });
                setChanges(changes.set('use_scheduling', use_scheduling));
              }}
              className="w-4 h-4 mr-2 accent-green-200"
            />
            {t('COPT_USE_SCHEDULING')}
          </label>
        )}
        {updatedData.grid_connection_capacity !== undefined &&
          updatedData.grid_connection_capacity_in === undefined && (
            <NumberInput
              value={updatedData.grid_connection_capacity === null ? null : updatedData.grid_connection_capacity * 1000}
              className={inputClass}
              name="grid_connection_capacity"
              label={t('COPT_GRID_CONNECTION_CAPACITY')}
              required
              onChange={(value: number) => {
                const grid_connection_capacity = value != null && !Number.isNaN(value) ? value / 1000 : null;
                setUpdatedData({
                  ...updatedData,
                  grid_connection_capacity,
                });
                setChanges(changes.set('grid_connection_capacity', grid_connection_capacity));
              }}
              disabled={readOnly('grid_connection_capacity')}
              rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
              endAdornment={<InfoTooltip field="grid_connection_capacity" />}
            />
          )}
        {updatedData.grid_connection_capacity_in !== undefined && (
          <NumberInput
            value={
              updatedData.grid_connection_capacity_in === null ? null : updatedData.grid_connection_capacity_in * 1000
            }
            className={inputClass}
            name="grid_connection_capacity_in"
            label={t('COPT_GRID_CONNECTION_CAPACITY_IN')}
            required
            onChange={(value: number) => {
              const grid_connection_capacity_in = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                grid_connection_capacity_in,
              });
              setChanges(changes.set('grid_connection_capacity_in', grid_connection_capacity_in));
            }}
            disabled={readOnly('grid_connection_capacity_in')}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="grid_connection_capacity_in" />}
          />
        )}
        {updatedData.grid_connection_capacity_out !== undefined && (
          <NumberInput
            value={
              updatedData.grid_connection_capacity_out === null ? null : updatedData.grid_connection_capacity_out * 1000
            }
            className={inputClass}
            name="grid_connection_capacity_out"
            label={t('COPT_GRID_CONNECTION_CAPACITY_OUT')}
            required
            onChange={(value: number) => {
              const grid_connection_capacity_out = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                grid_connection_capacity_out,
              });
              setChanges(changes.set('grid_connection_capacity_out', grid_connection_capacity_out));
            }}
            disabled={readOnly('grid_connection_capacity_out')}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="grid_connection_capacity_out" />}
          />
        )}
        {updatedData.self_consumption !== undefined && (
          <NumberInput
            value={updatedData.self_consumption === null ? null : updatedData.self_consumption * 1000}
            className={inputClass}
            name="self_consumption"
            label={t('COPT_SELF_CONSUMPTION')}
            required
            onChange={(value: Nullable<number>) => {
              const self_consumption = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                self_consumption,
              });
              setChanges(changes.set('self_consumption', self_consumption));
            }}
            disabled={readOnly('self_consumption')}
            notNegative
            endAdornment={<InfoTooltip field="self_consumption" />}
          />
        )}
        {updatedData.self_consumption_costs !== undefined && (
          <NumberInput
            value={updatedData.self_consumption_costs}
            className={inputClass}
            name="self_consumption_costs"
            label={t('COPT_SELF_CONSUMPTION_COSTS')}
            required
            onChange={(value: Nullable<number>) => {
              const self_consumption_costs = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                self_consumption_costs,
              });
              setChanges(changes.set('self_consumption_costs', self_consumption_costs));
            }}
            disabled={readOnly('self_consumption_costs')}
            notNegative
            endAdornment={<InfoTooltip field="self_consumption_costs" />}
          />
        )}
        {optimizationSchedule !== undefined && (
          <div className="xl:col-span-2">
            <h4 className="m-0 flex items-center text-sm font-semibold">
              {t('COPT_OPT_RUN_SCHEDULE')}
              <InfoTooltip text={t('COPT_OPT_RUN_SCHEDULE_TOOLTIP')} field="opt_run_schedule" />
            </h4>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 w-full md:flex md:flex-col md:justify-between gap-4 pt-2">
                <div>
                  <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
                    <input
                      type="checkbox"
                      checked={optimizationSchedule === null}
                      disabled={readOnly('opt_run_schedule', assetType !== AssetType.Biogas)}
                      onChange={() => {
                        const nearestTime = roundToNearestMinutes(new Date(), {
                          nearestTo: 15,
                          roundingMethod: 'floor',
                        });
                        const schedule =
                          optimizationSchedule === null
                            ? {
                                minutes: getMinutes(nearestTime),
                                hours: getHours(nearestTime),
                                daysOfWeek: undefined,
                              }
                            : null;
                        setOptimizationSchedule(schedule);
                        setChanges(
                          changes.set(
                            'opt_run_schedule',
                            schedule !== null ? `${schedule.minutes} ${schedule.hours} * * *` : null,
                          ),
                        );
                      }}
                      className="w-4 h-4 mr-2 accent-green-200"
                    />
                    {t('COPT_OPT_RUN_SCHEDULE_AUTO')}
                  </label>
                </div>
                <div className="mt-4 md:mt-0">
                  <TimePicker
                    label={t('COPT_OPT_RUN_SCHEDULE_TIME')}
                    value={
                      optimizationSchedule !== null && (optimizationSchedule.minutes || optimizationSchedule?.hours)
                        ? setHours(setMinutes(new Date(), optimizationSchedule.minutes), optimizationSchedule.hours)
                        : new Date()
                    }
                    disabled={
                      readOnly('opt_run_schedule', assetType !== AssetType.Biogas) || optimizationSchedule === null
                    }
                    hoursStep={1}
                    minutesStep={15}
                    onChange={({ value }) => {
                      const minutes = getMinutes(value);
                      const hours = getHours(value);
                      optimizationSchedule !== null &&
                        setOptimizationSchedule({
                          ...optimizationSchedule,
                          minutes,
                          hours,
                        });
                      setChanges(
                        changes.set('opt_run_schedule', `${minutes} ${hours} * * ${optimizationSchedule?.daysOfWeek}`),
                      );
                    }}
                  />
                </div>
              </div>
              <ValidatedWeekdaySelector
                name="weekSelector"
                selected={
                  optimizationSchedule?.daysOfWeek !== undefined
                    ? ([...optimizationSchedule.daysOfWeek].reduce(
                        (a, v) => ({ ...a, [v]: true }),
                        {},
                      ) as WeekdaySelection)
                    : undefined
                }
                disabled={readOnly('opt_run_schedule', assetType !== AssetType.Biogas) || optimizationSchedule === null}
                className={clsx(
                  'p-4 mt-4 md:mt-0 md:ml-4 rounded-md bg-white border-solid border border-divider md:w-1/2',

                  {
                    'opacity-70': readOnly('opt_run_schedule') || optimizationSchedule === null,
                  },
                )}
                onChange={(selected) => {
                  const daysOfWeek = Object.entries(selected)
                    .filter(([, isSelected]) => isSelected)
                    .map(([day]) => day);
                  if (optimizationSchedule !== null) {
                    setOptimizationSchedule({
                      ...optimizationSchedule,
                      daysOfWeek,
                    });
                    setChanges(
                      changes.set(
                        'opt_run_schedule',
                        `${optimizationSchedule.minutes} ${optimizationSchedule.hours} * * ${daysOfWeek}`,
                      ),
                    );
                  }
                }}
                rules={optimizationSchedule !== null ? weekdaySelectionRules : undefined}
              />
            </div>
          </div>
        )}

        {/* Parameters for Biogas asset types */}
        {assetType === AssetType.Biogas && (
          <div className="w-full xl:col-span-2 pb-4 ">
            <h4 className="m-0 flex items-center text-sm font-semibold pb-4">
              {t('COPT_HORIZON_MODE')}
              <InfoTooltip
                text={
                  <Trans components={{ strong: <strong />, ul: <ul />, li: <li /> }}>
                    {t('COPT_HORIZON_MODE_TOOLTIP', {
                      next_open_market: t('COPT_HORIZON_MODE_NEXT_OPEN_MARKET'),
                      next_monday: t('COPT_HORIZON_MODE_NEXT_MONDAY'),
                    })}
                  </Trans>
                }
                field="horizon_mode"
              />
            </h4>

            <FormControl className="w-full bg-white xl:col-span-2">
              <InputLabel id="seasons">{t('COPT_HORIZON_MODE')}</InputLabel>
              <Select
                id="horizon-mode-select"
                value={updatedData.horizon_mode}
                disabled={readOnly('horizon_mode')}
                label={t('COPT_HORIZON_MODE')}
                onChange={(e) => {
                  const horizon_mode = e.target.value as OptimizationHorizonMode;
                  setUpdatedData({
                    ...updatedData,
                    horizon_mode,
                  });
                  setChanges(changes.set('horizon_mode', horizon_mode));
                }}
              >
                {Object.entries(OptimizationHorizonMode).map((element) => (
                  <MenuItem key={element[0]} value={element[1]}>
                    {t(`COPT_HORIZON_MODE_${element[1].toUpperCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
        {(updatedData.afrr_active !== undefined ||
          updatedData.afrr_price_factor_pos !== undefined ||
          updatedData.afrr_price_factor_neg !== undefined ||
          updatedData.afrr_pos_implicit_costs !== undefined ||
          updatedData.afrr_neg_implicit_costs !== undefined) && (
          <h3 className="m-0 text-base font-semibold xl:col-span-2">{t('COPT_GROUP_AFRR_CONFIG')}</h3>
        )}
        {updatedData.afrr_active !== undefined && (
          <div className="xl:col-span-2">
            <label className="w-fit flex items-center text-sm font-light">
              <input
                type="checkbox"
                checked={!!updatedData.afrr_active}
                disabled={readOnly('afrr_active')}
                onChange={() => {
                  const afrr_active = !updatedData.afrr_active;
                  setUpdatedData({ ...updatedData, afrr_active });
                  setChanges(changes.set('afrr_active', afrr_active));
                }}
                className="w-4 h-4 mr-2 accent-green-200"
              />
              {t('COPT_AFRR_ACTIVE')}
              <InfoTooltip field="afrr_active" />
            </label>
          </div>
        )}
        {updatedData.afrr_price_factor_pos !== undefined && (
          <NumberInput
            value={updatedData.afrr_price_factor_pos}
            className={inputClass}
            name="afrr_price_factor_pos"
            label={t('COPT_AFRR_PRICE_FACTOR_POS')}
            onChange={(value: Nullable<number>) => {
              const afrr_price_factor_pos = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                afrr_price_factor_pos: afrr_price_factor_pos,
              });
              setChanges(changes.set('afrr_price_factor_pos', afrr_price_factor_pos));
            }}
            disabled={readOnly('afrr_price_factor_pos')}
            maxValue={1}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="afrr_price_factor_pos" />}
          />
        )}
        {updatedData.afrr_price_factor_neg !== undefined && (
          <NumberInput
            value={updatedData.afrr_price_factor_neg}
            className={inputClass}
            name="frr_price_factor_neg"
            label={t('COPT_AFRR_PRICE_FACTOR_NEG')}
            onChange={(value: Nullable<number>) => {
              const afrr_price_factor_neg = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                afrr_price_factor_neg,
              });
              setChanges(changes.set('afrr_price_factor_neg', afrr_price_factor_neg));
            }}
            disabled={readOnly('afrr_price_factor_neg')}
            maxValue={1}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="afrr_price_factor_neg" />}
          />
        )}
        {updatedData.afrr_pos_implicit_costs !== undefined && (
          <NumberInput
            value={updatedData.afrr_pos_implicit_costs}
            className={inputClass}
            name="afrr_pos_implicit_costs"
            label={t('COPT_AFRR_POS_IMPLICIT_COSTS')}
            onChange={(value: Nullable<number>) => {
              const afrr_pos_implicit_costs = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                afrr_pos_implicit_costs,
              });
              setChanges(changes.set('afrr_pos_implicit_costs', afrr_pos_implicit_costs));
            }}
            disabled={readOnly('afrr_pos_implicit_costs')}
            notNegative
            endAdornment={<InfoTooltip field="afrr_pos_implicit_costs" />}
          />
        )}
        {updatedData.afrr_neg_implicit_costs !== undefined && (
          <NumberInput
            value={updatedData.afrr_neg_implicit_costs}
            className={inputClass}
            name="afrr_neg_implicit_costs"
            label={t('COPT_AFRR_NEG_IMPLICIT_COSTS')}
            onChange={(value: Nullable<number>) => {
              const afrr_neg_implicit_costs = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                afrr_neg_implicit_costs,
              });
              setChanges(changes.set('afrr_neg_implicit_costs', afrr_neg_implicit_costs));
            }}
            disabled={readOnly('afrr_neg_implicit_costs')}
            notNegative
            endAdornment={<InfoTooltip field="afrr_neg_implicit_costs" />}
          />
        )}
        {updatedTargetRatedPowerGroups !== undefined && (
          <div className="pt-3 flex justify-between border-solid border-0 border-t border-divider xl:col-span-2">
            <h4 className="m-0 flex items-center text-sm font-semibold">
              {t('COPT_TARGET_RATED_POWER_GROUPS_TITLE')}
              <InfoTooltip text={t('COPT_TARGET_RATED_POWER_GROUPS_TOOLTIP')} field="target_rated_power_groups" />
            </h4>
            <Button
              color="primary"
              disabled={readOnly('target_rated_power_groups')}
              className="text-2xl flex h-10 w-10 min-w-10 shrink-0 grow-0 items-center justify-center rounded-full"
              onClick={() => {
                setUpdatedTargetRatedPowerGroups([
                  ...updatedTargetRatedPowerGroups,
                  {
                    identifier: faker.string.uuid(),
                    name: '',
                    target_rated_power_eeg: 0.1, // this value must be > 0
                    schedule_min_mw: null,
                    schedule_max_mw: null,
                    // These are the default values according to cOpt docs
                    penalty_upper: 5000,
                    penalty_lower: 5000,
                    eeg_regime: EEGRegime.Pre2016,
                    eeg_premium: 0,
                    eeg_2023_v2_threshold_ct_per_kwh: 2,
                  },
                ]);
              }}
            >
              +
            </Button>
          </div>
        )}
        {updatedTargetRatedPowerGroups?.map((group) => {
          return (
            <TargetRatedPowerGroup
              key={group.identifier}
              data={group}
              setUpdatedData={setUpdatedData}
              targetRatedPowerGroups={updatedTargetRatedPowerGroups}
              setTargetRatedPowerGroups={setUpdatedTargetRatedPowerGroups}
              readOnly={readOnly}
            />
          );
        })}

        {/* Parameters for InnoTender, InnoTenderGE and CoLocation asset types */}
        {(assetType === AssetType.InnoTender ||
          assetType === AssetType.InnoTenderGE ||
          assetType === AssetType.CoLocation) && (
          <>
            {updatedData.pv_forecast_buffer !== undefined && (
              <NumberInput
                value={updatedData.pv_forecast_buffer}
                className={inputClass}
                name="pv_forecast_buffer"
                label={t('COPT_PV_FORECAST_BUFFER')}
                onChange={(value: Nullable<number>) => {
                  const pv_forecast_buffer = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    pv_forecast_buffer,
                  });
                  setChanges(changes.set('pv_forecast_buffer', pv_forecast_buffer));
                }}
                disabled={readOnly('pv_forecast_buffer')}
                notNegative
                maxValue={1}
                endAdornment={<InfoTooltip field="pv_forecast_buffer" />}
              />
            )}
            <div className="xl:col-span-2">
              {updatedData.power_buffer_coupled_on_pv_on !== undefined && (
                <label className="flex items-center text-sm font-light mr-4">
                  <input
                    type="checkbox"
                    checked={!!updatedData.power_buffer_coupled_on_pv_on}
                    disabled={readOnly('power_buffer_coupled_on_pv_on')}
                    onChange={() => {
                      const power_buffer_coupled_on_pv_on = !updatedData.power_buffer_coupled_on_pv_on;
                      setUpdatedData({ ...updatedData, power_buffer_coupled_on_pv_on });
                      setChanges(changes.set('power_buffer_coupled_on_pv_on', power_buffer_coupled_on_pv_on));
                    }}
                    className="w-4 h-4 mr-2 accent-green-200"
                  />
                  {t('COPT_POWER_BUFFER_COUPLED_ON_PV_ON')}
                  <InfoTooltip field="power_buffer_coupled_on_pv_on" />
                </label>
              )}
            </div>
            {updatedData.power_buffer_percentage !== undefined && (
              <NumberInput
                value={
                  updatedData.power_buffer_percentage === null
                    ? null
                    : Math.round(updatedData.power_buffer_percentage * 100)
                }
                className={inputClass}
                name="power_buffer_percentage"
                label={t('COPT_POWER_BUFFER_PERCENTAGE')}
                required
                onChange={(value: Nullable<number>) => {
                  const power_buffer_percentage =
                    value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                  setUpdatedData({
                    ...updatedData,
                    power_buffer_percentage,
                  });
                  setChanges(changes.set('power_buffer_percentage', power_buffer_percentage));
                }}
                disabled={readOnly('power_buffer_percentage')}
                notNegative
                maxValue={100}
                endAdornment={<InfoTooltip field="power_buffer_percentage" />}
              />
            )}
            {updatedData.power_buffer_fixed !== undefined && (
              <NumberInput
                value={
                  updatedData.power_buffer_fixed === null ? null : Math.round(updatedData.power_buffer_fixed * 1000)
                }
                className={inputClass}
                name="power_buffer_fixed"
                label={t('COPT_POWER_BUFFER_FIXED')}
                required
                onChange={(value: number) => {
                  const power_buffer_fixed = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                  setUpdatedData({
                    ...updatedData,
                    power_buffer_fixed,
                  });
                  setChanges(changes.set('power_buffer_fixed', power_buffer_fixed));
                }}
                disabled={readOnly('power_buffer_fixed')}
                notNegative
                endAdornment={<InfoTooltip field="power_buffer_fixed" />}
              />
            )}
            {updatedData.ppa_price !== undefined && (
              <NumberInput
                value={updatedData.ppa_price}
                className={inputClass}
                name="ppa_price"
                label={t('COPT_PPA_PRICE')}
                onChange={(value: Nullable<number>) => {
                  const ppa_price = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({ ...updatedData, ppa_price });
                  setChanges(changes.set('ppa_price', ppa_price));
                }}
                disabled={readOnly('ppa_price')}
              />
            )}
            {updatedData.market_premium !== undefined && (
              <NumberInput
                value={updatedData.market_premium}
                className={inputClass}
                name="market_premium"
                label={t('COPT_MARKET_PREMIUM')}
                required
                onChange={(value: number) => {
                  const market_premium = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    market_premium,
                  });
                  setChanges(changes.set('market_premium', market_premium));
                }}
                disabled={readOnly('market_premium')}
              />
            )}
            {updatedData.robotron_name !== undefined && (
              <TextInput
                value={updatedData.robotron_name}
                className={inputClass}
                name="robotron_name"
                label={t('COPT_ROBOTRON_NAME')}
                disabled
              />
            )}
            {updatedData.inverter_efficiency !== undefined && (
              <NumberInput
                value={
                  updatedData.inverter_efficiency === null ? null : Math.round(updatedData.inverter_efficiency * 100)
                }
                className={inputClass}
                name="inverter_efficiency"
                label={t('COPT_INVERTER_EFFICIENCY')}
                required
                onChange={(value: number) => {
                  const inverter_efficiency = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                  setUpdatedData({
                    ...updatedData,
                    inverter_efficiency,
                  });
                  setChanges(changes.set('inverter_efficiency', inverter_efficiency));
                }}
                disabled={readOnly('inverter_efficiency')}
                maxValue={100}
                rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
              />
            )}
            {updatedData.profit_share !== undefined && (
              <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
                <input
                  type="checkbox"
                  checked={!!updatedData.profit_share}
                  disabled={readOnly('profit_share')}
                  onChange={() => {
                    const profit_share = !updatedData.profit_share;
                    setUpdatedData({ ...updatedData, profit_share });
                    setChanges(changes.set('profit_share', profit_share));
                  }}
                  className="w-4 h-4 mr-2 accent-green-200"
                />
                {t('COPT_PROFIT_SHARE')}
              </label>
            )}
            {updatedBillingDataConfig !== undefined && (
              <>
                <h4 className="font-normal text-lg p-0 m-0 xl:col-span-2">{t('COPT_BILLING_DATA_CONFIG_TITLE')}</h4>
                {updatedBillingDataConfig?.battery_charge_threshold !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.battery_charge_threshold === null
                        ? null
                        : updatedBillingDataConfig?.battery_charge_threshold * 1000
                    }
                    className={inputClass}
                    name="billing_data_config.battery_charge_threshold"
                    label={t('COPT_BILLING_DATA_CONFIG_BATTERY_CHARGE_THRESHOLD')}
                    required
                    onChange={(value: number) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        battery_charge_threshold: value != null && !Number.isNaN(value) ? value / 1000 : null,
                      });
                    }}
                    disabled={readOnly('battery_charge_threshold')}
                  />
                )}
                {updatedBillingDataConfig?.grid_connection_power_threshold !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.grid_connection_power_threshold === null
                        ? null
                        : updatedBillingDataConfig?.grid_connection_power_threshold * 1000
                    }
                    className={inputClass}
                    name="billing_data_config.grid_connection_power_threshold"
                    label={t('COPT_BILLING_DATA_CONFIG_GRID_CONNECTION_POWER_THRESHOLD')}
                    required
                    onChange={(value: number) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        grid_connection_power_threshold: value != null && !Number.isNaN(value) ? value / 1000 : null,
                      });
                    }}
                    disabled={readOnly('grid_connection_power_threshold')}
                    maxValue={0}
                  />
                )}
                {updatedBillingDataConfig?.battery_not_used_volume_threshold !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.battery_not_used_volume_threshold === null
                        ? null
                        : updatedBillingDataConfig?.battery_not_used_volume_threshold * 1000
                    }
                    className={inputClass}
                    name="billing_data_config.battery_not_used_volume_threshold"
                    label={t('COPT_BILLING_DATA_CONFIG_BATTERY_NOT_USED_VOLUME_THRESHOLD')}
                    onChange={(value: Nullable<number>) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        battery_not_used_volume_threshold: value != null && !Number.isNaN(value) ? value / 1000 : null,
                      });
                    }}
                    disabled={readOnly('battery_not_used_volume_threshold')}
                    notNegative
                  />
                )}
                {updatedBillingDataConfig?.pv_deviation_threshold_relative !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.pv_deviation_threshold_relative === null
                        ? null
                        : Math.round(updatedBillingDataConfig?.pv_deviation_threshold_relative * 100)
                    }
                    className={inputClass}
                    name="billing_data_config.pv_deviation_threshold_relative"
                    label={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_RELATIVE')}
                    onChange={(value: Nullable<number>) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        pv_deviation_threshold_relative:
                          value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null,
                      });
                    }}
                    disabled={readOnly('pv_deviation_threshold_relative')}
                    maxValue={100}
                    rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                  />
                )}
                {updatedBillingDataConfig?.pv_deviation_threshold_absolute !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.pv_deviation_threshold_absolute === null
                        ? null
                        : updatedBillingDataConfig?.pv_deviation_threshold_absolute * 1000
                    }
                    className={inputClass}
                    name="billing_data_config.pv_deviation_threshold_absolute"
                    label={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_ABSOLUTE')}
                    onChange={(value: Nullable<number>) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        pv_deviation_threshold_absolute: value != null && !Number.isNaN(value) ? value / 1000 : null,
                      });
                    }}
                    disabled={readOnly('pv_deviation_threshold_absolute')}
                    rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                  />
                )}
                {updatedBillingDataConfig?.grid_feedin_tolerance_absolute !== undefined && (
                  <NumberInput
                    value={
                      updatedBillingDataConfig?.grid_feedin_tolerance_absolute === null
                        ? null
                        : updatedBillingDataConfig?.grid_feedin_tolerance_absolute * 1000
                    }
                    className={inputClass}
                    name="billing_data_config.grid_feedin_tolerance_absolute"
                    label={t('COPT_BILLING_DATA_CONFIG_GRID_FEEDIN_TOLERANCE_ABSOLUTE')}
                    onChange={(value: Nullable<number>) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        grid_feedin_tolerance_absolute: value != null && !Number.isNaN(value) ? value / 1000 : null,
                      });
                    }}
                    disabled={readOnly('grid_feedin_tolerance_absolute')}
                    rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                  />
                )}
                {updatedBillingDataConfig?.availability_metric_method !== undefined && (
                  <FormControl className={inputClass}>
                    <InputLabel id="availability-metric-method">
                      {t('COPT_BILLING_DATA_CONFIG_AVAILABILITY_METRIC_METHOD')}
                    </InputLabel>
                    <Select
                      id="availability-metric-method"
                      value={updatedBillingDataConfig?.availability_metric_method}
                      disabled={readOnly('availability_metric_method')}
                      label={t('COPT_BILLING_DATA_CONFIG_AVAILABILITY_METRIC_METHOD')}
                      onChange={(e) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          availability_metric_method: e.target.value as AvailabilityMetricMethod,
                        });
                      }}
                    >
                      {Object.entries(AvailabilityMetricMethod).map((element) => (
                        <MenuItem value={element[0]} key={element[0]}>
                          {element[1]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {updatedBillingDataConfig?.ppa_billed !== undefined && (
                  <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
                    <input
                      type="checkbox"
                      checked={!!updatedBillingDataConfig?.ppa_billed}
                      disabled={readOnly('ppa_billed')}
                      onChange={() =>
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          ppa_billed: !updatedBillingDataConfig?.ppa_billed,
                        })
                      }
                      className="w-4 h-4 mr-2 accent-green-200"
                    />
                    {t('COPT_BILLING_DATA_CONFIG_PPA_BILLED')}
                  </label>
                )}
              </>
            )}
          </>
        )}
        {/* Parameter for Danpower Asset Types */}
        {assetType === AssetType.Danpower && (
          <>
            {updatedData.device_number !== undefined && (
              <p className="text-grey-400 text-sm font-semibold xl:col-span-2">
                {`${t('COPT_DEVICE_NUMBER')} `}
                <span className="text-grey-300 font-medium">{updatedData.device_number}</span>
              </p>
            )}
            {updatedData.peak_power !== undefined && (
              <NumberInput
                value={updatedData.peak_power === null ? null : updatedData.peak_power * 1000}
                className={inputClass}
                name="peak_power"
                label={t('COPT_PEAK_POWER')}
                onChange={(value: Nullable<number>) => {
                  const peak_power = value != null && !Number.isNaN(value) ? value / 1000 : null;
                  setUpdatedData({
                    ...updatedData,
                    peak_power,
                  });
                  setChanges(changes.set('peak_power', peak_power));
                }}
                disabled={readOnly('peak_power')}
                notNegative
                endAdornment={<InfoTooltip field="peak_power" />}
              />
            )}
            {updatedData.base_power !== undefined && (
              <NumberInput
                value={updatedData.base_power === null ? null : updatedData.base_power * 1000}
                className={inputClass}
                name="base_power"
                label={t('COPT_BASE_POWER')}
                onChange={(value: Nullable<number>) => {
                  const base_power = value != null && !Number.isNaN(value) ? value / 1000 : null;
                  setUpdatedData({
                    ...updatedData,
                    base_power,
                  });
                  setChanges(changes.set('base_power', base_power));
                }}
                disabled={readOnly('base_power')}
                notNegative
                endAdornment={<InfoTooltip field="base_power" />}
              />
            )}
            {updatedData.level_time_min !== undefined && (
              <NumberInput
                value={updatedData.level_time_min === null ? null : updatedData.level_time_min * 60}
                className={inputClass}
                name="level_time_min"
                label={t('COPT_LEVEL_TIME_MIN')}
                onChange={(value: Nullable<number>) => {
                  const level_time_min = value != null && !Number.isNaN(value) ? value / 60 : null;
                  setUpdatedData({
                    ...updatedData,
                    level_time_min,
                  });
                  setChanges(changes.set('level_time_min', level_time_min));
                }}
                disabled={readOnly('level_time_min')}
                notNegative
                endAdornment={<InfoTooltip field="level_time_min" />}
              />
            )}
            {updatedData.daily_peak_time_max !== undefined && (
              <NumberInput
                value={updatedData.daily_peak_time_max === null ? null : updatedData.daily_peak_time_max * 60}
                className={inputClass}
                name="daily_peak_time_max"
                label={t('COPT_DAILY_PEAK_TIME_MAX')}
                onChange={(value: Nullable<number>) => {
                  const daily_peak_time_max = value != null && !Number.isNaN(value) ? value / 60 : null;
                  setUpdatedData({
                    ...updatedData,
                    daily_peak_time_max,
                  });
                  setChanges(changes.set('daily_peak_time_max', daily_peak_time_max));
                }}
                disabled={readOnly('daily_peak_time_max')}
                notNegative
                endAdornment={<InfoTooltip field="daily_peak_time_max" />}
              />
            )}
            {updatedData.daily_level_changes_max !== undefined && (
              <NumberInput
                value={updatedData.daily_level_changes_max}
                className={inputClass}
                name="daily_level_changes_max"
                label={t('COPT_DAILY_LEVEL_CHANGES_MAX')}
                onChange={(value: Nullable<number>) => {
                  const daily_level_changes_max = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    daily_level_changes_max,
                  });
                  setChanges(changes.set('daily_level_changes_max', daily_level_changes_max));
                }}
                disabled={readOnly('daily_level_changes_max')}
                notNegative
                rules={[mustBeDivisibleBy(2, tv('COMMON_MUST_BE_EVEN'))]}
                endAdornment={<InfoTooltip field="daily_level_changes_max" />}
              />
            )}
            {updatedData.eeg_premium !== undefined && (
              <NumberInput
                value={updatedData.eeg_premium}
                className={inputClass}
                name="eeg_premium"
                label={t('COPT_EEG_PREMIUM')}
                onChange={(value: Nullable<number>) => {
                  const eeg_premium = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    eeg_premium,
                  });
                  setChanges(changes.set('eeg_premium', eeg_premium));
                }}
                disabled={readOnly('eeg_premium')}
                notNegative
                endAdornment={<InfoTooltip field="eeg_premium" />}
              />
            )}
            {updatedData.marginal_price !== undefined && (
              <NumberInput
                value={updatedData.marginal_price}
                className={inputClass}
                name="marginal_price"
                label={t('COPT_MARGINAL_PRICE')}
                onChange={(value: Nullable<number>) => {
                  const marginal_price = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    marginal_price,
                  });
                  setChanges(changes.set('marginal_price', marginal_price));
                }}
                disabled={readOnly('marginal_price')}
                notNegative
                endAdornment={<InfoTooltip field="marginal_price" />}
              />
            )}
            {updatedData.eeg_negative_hour_rule_length !== undefined && (
              <NumberInput
                value={updatedData.eeg_negative_hour_rule_length}
                className={inputClass}
                name="eeg_negative_hour_rule_length"
                label={t('COPT_EEG_NEGATIVE_HOUR_RULE_LENGTH')}
                onChange={(value: Nullable<number>) => {
                  const eeg_negative_hour_rule_length = value != null && !Number.isNaN(value) ? value : null;
                  setUpdatedData({
                    ...updatedData,
                    eeg_negative_hour_rule_length,
                  });
                  setChanges(changes.set('eeg_negative_hour_rule_length', eeg_negative_hour_rule_length));
                }}
                disabled={readOnly('eeg_negative_hour_rule_length')}
                notNegative
                endAdornment={<InfoTooltip field="eeg_negative_hour_rule_length" />}
              />
            )}
            {updatedData.federal_state !== undefined && (
              <FormControl className="mb-4 w-full bg-white">
                <InputLabel id="federal_state_holidays">{t('COPT_FEDERAL_STATE')}</InputLabel>
                <Select
                  id="federal-state-select"
                  value={updatedData.federal_state ?? ''}
                  disabled={readOnly('federal_state')}
                  label={t('COPT_FEDERAL_STATE')}
                  onChange={(e) => {
                    const federal_state = e.target.value as GermanyFederalState;
                    setUpdatedData({
                      ...updatedData,
                      federal_state,
                    });
                    setChanges(changes.set('federal_state', federal_state));
                  }}
                  endAdornment={<InfoTooltip componentClass="flex items-center mr-4" field="federal_state" />}
                >
                  {Object.entries(GermanyFederalState).map((element) => (
                    <MenuItem value={element[0]} key={element[0]}>
                      {element[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}
      </ValidatedContainer>
      {!readOnly() && (
        <div className="flex gap-2 justify-between xl:col-span-2">
          <Button color="primary" variant="outlined" className="bg-white" onClick={resetValues}>
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            color="primary"
            disabled={!isValid || isSaving}
            onClick={() => updateOptimizationGroupParametersValues(isValid)}
            startIcon={isSaving && <CircularProgress size={16} />}
          >
            {t('SHARED_SAVE')}
          </Button>
        </div>
      )}
    </div>
  );
}
